import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

const PROMPTS = {
  uphill: 'What is still unknown here?',
  peak: 'What made the unknowns go away?',
  downhill: 'What is left to do?',
  done: 'Anything worth remembering about how this went?',
};

const promptFor = (position) => {
  if (position >= 0.95) return PROMPTS.done;
  if (position < 0.45) return PROMPTS.uphill;
  if (position < 0.6) return PROMPTS.peak;
  return PROMPTS.downhill;
};

/**
 * Asks why a dot moved, right after it moved.
 *
 * The move is already saved by the time this opens, so skipping costs nothing and
 * the question never blocks the gesture. It is worth asking anyway: positions tell
 * you a scope stalled in week three, and only the note tells you it stalled on a
 * vendor sandbox nobody had access to. Six weeks later that is the whole story,
 * and nobody remembers it.
 */
const MoveNoteDialog = ({ open, request, onSave, onSkip }) => {
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) setNote('');
  }, [open, request?.scope?.id]);

  if (!request) return null;

  const { scope, position } = request;
  const percent = Math.round((position ?? 0) * 100);
  const submit = () => {
    const trimmed = note.trim();
    if (trimmed) onSave(request, trimmed);
    else onSkip();
  };

  return (
    <Dialog open={open} onClose={onSkip} maxWidth="sm" fullWidth>
      <DialogTitle>{promptFor(position ?? 0)}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {`${scope.jiraKey} is now ${percent}% along the hill. A line about why makes this
            readable in a retrospective, when the position alone will not be.`}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) submit();
          }}
          placeholder="Ran the migration on a copy of production, so the shape of the data is no longer a guess"
          inputProps={{ maxLength: 280 }}
          helperText={`${note.length}/280`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onSkip} color="inherit">Skip</Button>
        <Button onClick={submit} variant="contained" disabled={!note.trim()}>
          Save the note
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveNoteDialog;
